import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import * as Pet from '../pet';
import { scheduleStatusUpdateMessage } from '../petChatbot/petChatbot.scheduledActions';
import { Locale } from '../ruleset/locale';
import { getHadVisitorChatbotPayload } from './visit.analytics';
import { visitSession } from './visit.schema';
import { getVisitedCount, getVisitsCount, isURLFriend } from './visit.getters';
import { sendAnalyticsEvents } from '../analytics';
import { isBuruFriend } from '../friends/friends.getters';
import { GENERIC_AVATAR } from '../profile/assets';

const schema = {
  endVisit: SB.object({ visitSession, sendMessage: SB.boolean() }),
  sendFriendReviveNotification: SB.object({ friendId: SB.string() }),
  sendFriendBackToLifeNotification: SB.object({ friendId: SB.string() }),
};

const actions = createScheduledActions(schema)({
  endVisit: async (state, { visitSession, sendMessage }, api) => {
    const {
      friendId,
      careActions,
      coinsSpent,
      gemsReceived,
      friendDialogs,
      paidCareActions,
    } = visitSession;
    const { visitDetails: friendPet, nextFriendPetState } =
      await api.asyncGetters.getPetVisitDetailsAndNextFriendPetState(friendId);

    if (!friendPet) {
      return;
    }

    // Send analytics event.
    const {
      getCurrentEvolution,
      getPetAge,
      getAttributeValues,
      getStressValue,
    } = Pet.getters;
    const playerAttributes = getAttributeValues(state, api.date.now());
    const playerSpecies = getCurrentEvolution(state)?.species;

    const visits = getVisitsCount(state, friendId);
    const visited = getVisitedCount(state, friendId);
    const friendAttributes = friendPet.attributes;
    const friendSpecies = friendPet.currentEvolution.species;

    sendAnalyticsEvents(api, [
      // Event will be added to visited friend's stream.
      {
        eventType: 'VisitedByFriend',
        userId: friendId,
        // Friend in this case is the player.
        eventProperties: {
          friendID: state.id,
          friendPetID: playerSpecies ?? 'unknown',
          friendAge: getPetAge(state, api.date.now()),
          friendPetStomach: playerAttributes.stomach,
          friendPetLove: playerAttributes.love,
          friendDirtiness: playerAttributes.dirtiness,
          friendUrine: playerAttributes.urine,
          friendStress: getStressValue(state, api.date.now()),
          friendToothAcidity: playerAttributes.toothAcidity,
          visits: visited,
          visited: visits,
          careActions: careActions ?? 0,
          bPointsConsumed: coinsSpent ?? 0,
          gemsReceived: gemsReceived ?? 0,
          friendDialogs: friendDialogs ?? 0,
          urlInviteFriend: isURLFriend(state, friendId),
        },
      },
      // Event will be added to player's stream.
      {
        eventType: 'FriendVisitFinish',
        eventProperties: {
          friendID: friendId,
          friendPetID: friendSpecies,
          friendAge: friendPet.age,
          friendPetStomach: friendAttributes.stomach,
          friendPetLove: friendAttributes.love,
          friendDirtiness: friendAttributes.dirtiness,
          friendUrine: friendAttributes.urine,
          friendStress: friendAttributes.stress,
          friendToothAcidity: friendAttributes.toothAcidity,
          visits,
          visited,
          careActions: careActions ?? 0,
          paidCareActions: paidCareActions ?? 0,
          bPointsConsumed: coinsSpent ?? 0,
          gemsReceived: gemsReceived ?? 0,
          friendDialogs: friendDialogs ?? 0,
          friend: isBuruFriend(state, friendId),
          urlInviteFriend: isURLFriend(state, friendId),
        },
      },
    ]);

    // Check that friend is offline before scheduling chatbot messages
    if (friendPet.isOnline) {
      return;
    }

    // Send had_visitor notification if friend has notifications turned on.
    if (friendPet.notificationSettings !== 'off' && sendMessage) {
      const profile = state.publicProfile ?? state.profile;
      const payload = getHadVisitorChatbotPayload({
        friendId,
        friendPet,
        visits,
        visited,
        isFriend: isBuruFriend(state, friendId),
        now: api.date.now(),
        state,
      });

      const message = getChatbotFlexMessage({
        template: 'visited',
        payload,
        data: {
          locale: friendPet.locale as Locale,
          petName: friendAttributes.name,
          species: friendSpecies,
          visitor: {
            name: profile?.name ?? 'Unknown',
            photo: profile?.photo ?? GENERIC_AVATAR,
            pet: playerSpecies ?? 'ball',
          },
        },
      });

      deliverChatbotMessage(state, api, {
        receiverId: friendId,
        message,
        channel: friendPet.notificationChannel,
      });
    }

    // Schedule status notifications for the friend's pet
    if (nextFriendPetState) {
      scheduleStatusUpdateMessage(
        {
          nextPetState: nextFriendPetState,
          now: api.date.now(),
          currentSpecies: friendSpecies,
        },
        api,
        friendId,
      );
    }
  },

  sendFriendReviveNotification: async (state, { friendId }, api) => {
    const friendPet = await api.asyncGetters.getPetVisitDetails(friendId);

    // No friend pet found. Bail.
    if (!friendPet) return;

    // Friend is online. Bail.
    if (friendPet.isOnline) return;

    // Notifications are off. Bail.
    if (friendPet.notificationSettings === 'off') return;

    // Get data for chatbot message.
    const { getCurrentEvolution } = Pet.getters;
    const playerSpecies = getCurrentEvolution(state)?.species;
    const visits = getVisitsCount(state, friendId);
    const visited = getVisitedCount(state, friendId);
    const friendAttributes = friendPet.attributes;
    const friendSpecies = friendPet.currentEvolution.species;
    const profile = state.publicProfile ?? state.profile;

    // Reuse the `hadVisitor` payload for the chatbot message.
    const payload = getHadVisitorChatbotPayload({
      friendId,
      friendPet,
      visits,
      visited,
      isFriend: isBuruFriend(state, friendId),
      now: api.date.now(),
      state,
    });

    // Render the chatbot message.
    const message = getChatbotFlexMessage({
      template: 'revived',
      payload,
      data: {
        locale: friendPet.locale as Locale,
        petName: friendAttributes.name,
        species: friendSpecies,
        visitor: {
          name: profile?.name ?? 'Unknown',
          photo: profile?.photo ?? GENERIC_AVATAR,
          pet: playerSpecies ?? 'ball',
        },
      },
    });

    // Deliver the chatbot message forcing OA.
    deliverChatbotMessage(state, api, {
      receiverId: friendId,
      message,
      channel: 'oa',
    });
  },

  sendFriendBackToLifeNotification: async (state, { friendId }, api) => {
    const friendPet = await api.asyncGetters.getPetVisitDetails(friendId);

    // No friend pet found. Bail.
    if (!friendPet) return;

    // Friend is online. Bail.
    if (friendPet.isOnline) return;

    // Notifications are off. Bail.
    if (friendPet.notificationSettings === 'off') return;

    // Get data for chatbot message.
    const visits = getVisitsCount(state, friendId);
    const visited = getVisitedCount(state, friendId);
    const friendAttributes = friendPet.attributes;
    const friendSpecies = friendPet.currentEvolution.species;

    // Reuse the `hadVisitor` payload for the chatbot message.
    const payload = getHadVisitorChatbotPayload({
      friendId,
      friendPet,
      visits,
      visited,
      isFriend: isBuruFriend(state, friendId),
      now: api.date.now(),
      state,
    });

    // Render the chatbot message.
    const message = getChatbotFlexMessage({
      template: 'backToLife',
      payload,
      data: {
        locale: friendPet.locale as Locale,
        petName: friendAttributes.name,
        species: friendSpecies,
      },
    });

    // Deliver the chatbot message forcing OA.
    deliverChatbotMessage(state, api, {
      receiverId: friendId,
      message,
      channel: 'oa',
    });
  },
});

export const visitScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(actions),
};
